import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ChevronDown, ChevronUp, ChevronsUpDown, Download } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  aggregateByDay,
  aggregateStats,
  BoxStat,
  HISTORY_QUERY_LIMIT,
  useBoxStatusHistory,
} from '@/lib/api/stats';

type Period = '24h' | '7d' | '30d' | 'custom';
type View = 'summary' | 'daily';
type SortKey =
  | 'name'
  | 'ip'
  | 'onlinePct'
  | 'commandFailPct'
  | 'pingFailPct'
  | 'incidents'
  | 'longestOutageMs'
  | 'total';

const PRESET_MS: Record<Exclude<Period, 'custom'>, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

function formatDuration(ms: number): string {
  if (!ms) return '—';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) return `${hours}h ${remMinutes}m`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return `${days}d ${remHours}h`;
}

function csvCell(value: string | number): string {
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename: string, rows: (string | number)[][]) {
  const csv = rows.map((row) => row.map(csvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Red heatmap fill scaled by downtime; no data -> empty. */
function failStyle(failPct: number): React.CSSProperties {
  return { backgroundColor: `rgba(239, 68, 68, ${(failPct / 100).toFixed(3)})` };
}

export function Stats() {
  const [period, setPeriod] = useState<Period>('7d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [view, setView] = useState<View>('summary');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'name',
    dir: 'asc',
  });

  const range = useMemo(() => {
    if (period === 'custom') {
      const start = customFrom ? new Date(`${customFrom}T00:00:00`) : null;
      const end = customTo ? new Date(`${customTo}T23:59:59`) : null;
      return { start, end };
    }
    const end = new Date();
    const start = new Date(end.getTime() - PRESET_MS[period]);
    return { start, end };
  }, [period, customFrom, customTo]);

  const valid = !!range.start && !!range.end && range.start < range.end;

  const [events, loading] = useBoxStatusHistory(
    range.start ?? new Date(0),
    range.end ?? new Date(0)
  );

  const stats = useMemo(
    () => (valid && events ? aggregateStats(events) : []),
    [valid, events]
  );

  const daily = useMemo(
    () =>
      valid && events && range.start && range.end
        ? aggregateByDay(events, range.start, range.end)
        : { days: [], rows: [] },
    [valid, events, range.start, range.end]
  );

  const sortedStats = useMemo(() => {
    const arr = [...stats];
    arr.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [stats, sort]);

  const onSort = (key: SortKey) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' || key === 'ip' ? 'asc' : 'desc' }
    );

  const exportCsv = () => {
    if (view === 'summary') {
      const header = [
        'Name',
        'IP',
        'Samples',
        'Online %',
        'Command Fail %',
        'Ping Fail %',
        'Failure Incidents',
        'Longest Outage',
      ];
      const rows = sortedStats.map((s) => [
        s.name,
        s.ip,
        s.total,
        s.onlinePct.toFixed(1),
        s.commandFailPct.toFixed(1),
        s.pingFailPct.toFixed(1),
        s.incidents,
        formatDuration(s.longestOutageMs),
      ]);
      downloadCsv('box-stats.csv', [header, ...rows]);
    } else {
      const header = ['Name', 'IP', ...daily.days, 'Overall'];
      const rows = daily.rows.map((r) => [
        r.name,
        r.ip,
        ...daily.days.map((d) =>
          r.perDay[d] ? r.perDay[d].failPct.toFixed(1) : ''
        ),
        r.overall.failPct.toFixed(1),
      ]);
      downloadCsv('box-stats-daily.csv', [header, ...rows]);
    }
  };

  const sortIcon = (key: SortKey) =>
    sort.key === key ? (
      sort.dir === 'asc' ? (
        <ChevronUp className="h-3.5 w-3.5" />
      ) : (
        <ChevronDown className="h-3.5 w-3.5" />
      )
    ) : (
      <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
    );

  const SortHead = ({
    sortKey,
    label,
    right,
  }: {
    sortKey: SortKey;
    label: string;
    right?: boolean;
  }) => (
    <TableHead className={right ? 'text-right' : undefined}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 hover:text-foreground',
          right && 'flex-row-reverse'
        )}
      >
        {label}
        {sortIcon(sortKey)}
      </button>
    </TableHead>
  );

  return (
    <div className="w-full pb-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-bold">Box Stats</h1>
        <Button
          variant="outline"
          onClick={exportCsv}
          disabled={view === 'summary' ? stats.length === 0 : daily.rows.length === 0}
        >
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <Separator className="my-6" />

      <div className="flex flex-wrap items-center gap-3 pb-6">
        <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Last 24 hours</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="custom">Custom range</SelectItem>
          </SelectContent>
        </Select>

        {period === 'custom' && (
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="w-[170px]"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="w-[170px]"
            />
          </div>
        )}

        <div className="ml-auto flex items-center gap-1 rounded-md border p-1">
          <Button
            variant={view === 'summary' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setView('summary')}
          >
            Summary
          </Button>
          <Button
            variant={view === 'daily' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setView('daily')}
          >
            Daily
          </Button>
        </div>
      </div>

      {events && events.length >= HISTORY_QUERY_LIMIT && (
        <p className="pb-4 text-sm text-amber-500">
          Too much data for this range — only the most recent{' '}
          {HISTORY_QUERY_LIMIT.toLocaleString()} samples are included. Narrow
          the date range for complete stats.
        </p>
      )}

      {!valid ? (
        <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
          Pick a valid date range.
        </div>
      ) : loading ? (
        <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      ) : view === 'summary' ? (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <SortHead sortKey="name" label="Box" />
                <SortHead sortKey="ip" label="IP" />
                <SortHead sortKey="onlinePct" label="Online %" right />
                <SortHead sortKey="commandFailPct" label="Cmd Fail %" right />
                <SortHead sortKey="pingFailPct" label="Ping Fail %" right />
                <SortHead sortKey="incidents" label="Incidents" right />
                <SortHead sortKey="longestOutageMs" label="Longest Outage" right />
                <SortHead sortKey="total" label="Samples" right />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedStats.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-24 text-center">
                    No data for this period.
                  </TableCell>
                </TableRow>
              ) : (
                sortedStats.map((s: BoxStat) => (
                  <TableRow key={s.ip}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {s.ip}
                    </TableCell>
                    <TableCell className="text-right">
                      {s.onlinePct.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right">
                      {s.commandFailPct.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right">
                      {s.pingFailPct.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right">{s.incidents}</TableCell>
                    <TableCell className="text-right">
                      {formatDuration(s.longestOutageMs)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {s.total}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Each cell is that day&apos;s downtime (% of samples not online).
            Redder = worse. Rows are sorted worst-first. Hover a cell for detail.
          </p>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 z-10 bg-background">
                    Box
                  </TableHead>
                  {daily.days.map((d) => (
                    <TableHead key={d} className="text-center whitespace-nowrap">
                      {format(parseISO(d), 'EEE M/d')}
                    </TableHead>
                  ))}
                  <TableHead className="text-center">Overall</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {daily.rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={daily.days.length + 2}
                      className="h-24 text-center"
                    >
                      No data for this period.
                    </TableCell>
                  </TableRow>
                ) : (
                  daily.rows.map((r) => (
                    <TableRow key={r.ip}>
                      <TableCell className="sticky left-0 z-10 bg-background font-medium whitespace-nowrap">
                        {r.name}
                      </TableCell>
                      {daily.days.map((d) => {
                        const m = r.perDay[d];
                        return (
                          <TableCell
                            key={d}
                            className="text-center text-xs"
                            style={m ? failStyle(m.failPct) : undefined}
                            title={
                              m
                                ? `${r.name} ${d}\n` +
                                  `downtime ${m.failPct.toFixed(1)}% · ` +
                                  `${m.incidents} incident(s) · ` +
                                  `longest ${formatDuration(m.longestOutageMs)}`
                                : `${r.name} ${d}\nno data`
                            }
                          >
                            {m ? `${m.failPct.toFixed(0)}%` : '·'}
                          </TableCell>
                        );
                      })}
                      <TableCell
                        className="text-center text-xs font-medium"
                        style={failStyle(r.overall.failPct)}
                      >
                        {r.overall.failPct.toFixed(0)}%
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Stats;
